import React from 'react';
import { Scale, Medal, Users, Landmark } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const AboutPage = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="About Pattaya Solicitors | Law Firm Pattaya for Foreigners & Expats"
        description="About Pattaya Solicitors - Leading law firm in Pattaya Thailand for foreigners. 20+ years serving expats. English speaking solicitors, embassy recommended, Lawyer's Council licensed."
        keywords="About Pattaya Solicitors, Law Firm Pattaya for Foreigners, Expat Solicitor Pattaya, English Speaking Lawyer Pattaya, Solicitors in Pattaya Thailand, DMC Inter Law"
        path="/about"
        schemaType="about"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#000A22] via-[#001438] to-[#000A22] text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-flex items-center gap-2 bg-[#F5E7D2] text-[#000A22] px-5 py-2.5 rounded-full font-medium text-sm">
              <Scale className="w-4 h-4 text-[#D4A74A]" />
              About Our Firm
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold">About Pattaya Solicitors</h1>
            <p className="text-xl text-gray-300">Law Firm Pattaya for Foreigners | English Speaking Solicitors</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#D4A74A] to-[#C69A3E] opacity-20 blur-2xl rounded-3xl"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_pattaya-solicitors-1/artifacts/qbndhwu7_chantima-attorney.jpg"
                alt="Expat solicitor Pattaya legal advice"
                className="rounded-3xl shadow-2xl w-full relative z-10 ring-4 ring-[#D4A74A]/30"
              />
            </div>
            
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-[#000A22]">Attorney-at-Law</h2>
              <div className="prose prose-lg text-gray-700 space-y-4">
                <p>
                  Pattaya Solicitors (DMC Inter Law & Consultants) proudly represents both Thai and foreign residents throughout all legal matters in Thailand. Our team of experienced attorneys is dedicated to providing exceptional legal services with professionalism and integrity.
                </p>
                <p>
                  We are registered with the Lawyer's Council of Thailand as a fully legitimate law office, ensuring that all our services meet the highest professional and ethical standards.
                </p>
                <p>
                  As a mainstay on the Pattaya legal scene for many years, we have established ourselves as one of the top legal firms on Thailand's Eastern Seaboard. Our reputation is built on consistent results, professional excellence, and unwavering commitment to our clients.
                </p>
                <p>
                  We are regularly recommended by Embassies and through word-of-mouth referrals from satisfied clients who trust us to handle their most important legal matters.
                </p>
              </div>
            </div>
          </div>

          {/* Office Images */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_pattaya-solicitors-1/artifacts/sz6rc02d_office-staff.jpg"
                alt="Pattaya lawyers Thailand meeting client"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_d7574d0a-c2db-4bd5-bcf9-40578eb82c27/artifacts/bies7pjo_pattaya%20solicitors%20front%20office%20reception.jpg"
                alt="Pattaya solicitors front office reception lounge"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_d7574d0a-c2db-4bd5-bcf9-40578eb82c27/artifacts/xfusbpic_pattaya%20solicitors%20main%20office%20desk%20front.jpg"
                alt="Pattaya solicitors main office desk reception"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Key Features - Matching Homepage Trust Bar */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center border-2 hover:border-[#D4A74A] transition-all duration-300 hover:shadow-xl">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#F5E7D2] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Medal className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-[#000A22] mb-2">Fully Licensed</h3>
                <p className="text-gray-600">Registered with the Lawyer's Council of Thailand</p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 hover:border-[#D4A74A] transition-all duration-300 hover:shadow-xl">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#F5E7D2] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#D4A74A]">20+</span>
                </div>
                <h3 className="text-lg font-bold text-[#000A22] mb-2">Established Reputation</h3>
                <p className="text-gray-600">Years of trusted service in Pattaya</p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 hover:border-[#D4A74A] transition-all duration-300 hover:shadow-xl">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#F5E7D2] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Landmark className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-[#000A22] mb-2">Embassy Recommended</h3>
                <p className="text-gray-600">Trusted by foreign embassies</p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 hover:border-[#D4A74A] transition-all duration-300 hover:shadow-xl">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#F5E7D2] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Scale className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-[#000A22] mb-2">Top Legal Firm</h3>
                <p className="text-gray-600">Leading firm on Eastern Seaboard</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-4xl font-bold text-[#000A22]">Our Commitment</h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              At Pattaya Solicitors, we are committed to providing professional, reliable, and effective legal services to all our clients. Whether you are a local resident or an international expat, we treat every case with the utmost care and attention it deserves. Our goal is to ensure fair and just outcomes whilst minimising stress and complexity for our clients.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
